/**
 * Canonical Riftbound set release calendar — the single source of truth for
 * "is this set released, in preview, or upcoming yet".
 *
 * Framework-free on purpose so it can be imported unmodified from:
 *   - `src/constants/index.ts` (frontend `SETS` — see the sibling `.d.ts`)
 *   - `api/_shared/*.js` (serverless functions, e.g. deriving `card.previewed`)
 *   - `scripts/*.mjs` (seed/sync/build tooling)
 *
 * Keep this the ONLY place release/preview dates are hardcoded. Everything
 * else should derive from it rather than duplicating dates — duplicated
 * copies of this calendar (in `src/constants/index.ts`, a per-script
 * `VEN_RELEASE_DATE` constant, and a SQL ordinal CASE list in
 * `api/_shared/cardCache.js`) are exactly what caused the VEN scanner-catalog
 * gap and a stale-`previewed` regression risk found in August 2026 — see
 * `docs/new-set-release-checklist.md`.
 *
 * When a new set is announced, add one entry here (and nowhere else).
 * Constructed expansions that start a new meta also set `startsMeta: true`
 * — era bounds are derived in `shared/metaPeriods.js` (start at this set's
 * English release, exclusive end at the next expansion). Never hardcode dates.
 *
 * `releaseDate` is the English printing. Other languages ship on their own
 * day: put that day on `printDates`. Omit a language to share the English
 * date. A `{ label }` is an announced window with no day yet; it does not
 * move release status or the constructed meta.
 *
 * `collectorTotal` is the printed set size (`214/298`). Collector identity
 * is `SET-NUMBER`; the total is not part of the id. Sets without one
 * (Arcane, unreleased expansions, promo catalogs) omit `/total`.
 */
export const SET_RELEASE_CALENDAR = [
  { code: 'ARC', releaseDate: '2025-01-13' },
  {
    code: 'OGN',
    releaseDate: '2025-10-31',
    printDates: { zh: '2025-08-01', fr: '2026-05-29', ko: '2026-09-18' },
    startsMeta: true,
    collectorTotal: 298,
  },
  {
    code: 'OGS',
    releaseDate: '2025-10-31',
    printDates: { zh: '2025-08-01', fr: '2026-05-29', ko: '2026-09-18' },
    collectorTotal: 24,
  },
  {
    code: 'SFD',
    releaseDate: '2026-02-13',
    printDates: { zh: '2025-12-15', fr: '2026-07-24', ko: { label: 'December 2026' } },
    startsMeta: true,
    collectorTotal: 221,
  },
  {
    code: 'UNL',
    releaseDate: '2026-05-08',
    printDates: { zh: '2026-04-10', fr: '2026-07-24' },
    startsMeta: true,
    collectorTotal: 219,
  },
  {
    code: 'VEN',
    releaseDate: '2026-07-31',
    printDates: { fr: '2026-10-23' },
    startsMeta: true,
    collectorTotal: 166,
  },
  { code: 'RAD', releaseDate: '2026-10-23', startsMeta: true, collectorTotal: 167 },
  { code: 'LGC', releaseDate: '2027-01-29', startsMeta: true },
  { code: 'PG2', releaseDate: '2027-02-19' },
  { code: 'REC', releaseDate: '2027-04-30', startsMeta: true },
  { code: 'S08', releaseDate: '2027-07-01', releaseDateLabel: 'Q3 2027', startsMeta: true },
  { code: 'S09', releaseDate: '2027-10-01', releaseDateLabel: 'Q4 2027', startsMeta: true },
  // Evergreen promo catalogs — no releaseDate means "always released".
  { code: 'JDG' },
  { code: 'OPP' },
  { code: 'PR' },
];

const BY_CODE = new Map(SET_RELEASE_CALENDAR.map((s) => [s.code.toUpperCase(), s]));

/** UTC calendar day as YYYY-MM-DD — release comparisons are date-based, not wall-clock. */
export function calendarDayIso(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

/** Raw calendar entry for a set code, or undefined if not tracked (treated as evergreen/released). */
export function setCalendarEntry(setCode) {
  if (!setCode) return undefined;
  return BY_CODE.get(String(setCode).trim().toUpperCase());
}

/** Printed set size (`298` in `OGN · 214/298`). Undefined when the set has no `/total`. */
export function setCollectorTotal(setCode) {
  const total = setCalendarEntry(setCode)?.collectorTotal;
  return Number.isFinite(total) ? total : undefined;
}

/**
 * Parent set for a promo id whose only clue is the printed set size
 * (`opp-124-221` → SFD). Undefined when no set, or more than one, has that size.
 */
export function setCodeForCollectorTotal(total) {
  const size = Number(total);
  if (!Number.isFinite(size)) return undefined;
  const matches = SET_RELEASE_CALENDAR.filter((entry) => entry.collectorTotal === size);
  return matches.length === 1 ? matches[0].code : undefined;
}

/**
 * - no entry, or no `releaseDate` → 'released' (promos / evergreen / unknown codes)
 * - today >= `releaseDate` → 'released'
 * - today >= `previewDate` (optional) and before release → 'preview'
 * - else → 'upcoming'
 */
export function setReleaseStatus(setCode) {
  const meta = setCalendarEntry(setCode);
  if (!meta?.releaseDate) return 'released';

  const today = calendarDayIso();
  if (today >= meta.releaseDate) return 'released';
  if (meta.previewDate && today >= meta.previewDate) return 'preview';
  return 'upcoming';
}

export function isSetReleased(setCode) {
  return setReleaseStatus(setCode) === 'released';
}

/**
 * When one language of a set is printed.
 * English uses `releaseDate`. Another language uses `printDates` when it
 * ships on a different day, and otherwise the English date.
 * `{ label }` is an announced window with no day yet.
 *
 * @param {string | undefined} setCode
 * @param {string | undefined} language
 * @returns {{ date?: string, label?: string } | undefined}
 */
export function setLanguagePrintDate(setCode, language) {
  const entry = setCalendarEntry(setCode);
  if (!entry) return undefined;
  const code = String(language || 'en').trim().toLowerCase();
  if (code === 'en') return entry.releaseDate ? { date: entry.releaseDate } : undefined;
  const printed = entry.printDates?.[code];
  if (typeof printed === 'string') return { date: printed };
  if (printed && typeof printed.label === 'string') return { label: printed.label };
  return entry.releaseDate ? { date: entry.releaseDate } : undefined;
}
